
/*
 * VNCtask : VNCtask – the easy to use Task Management & To-Do List application. Stay organized. Anytime! Anywhere!
 * Copyright (C) 2015-2020 VNC – Virtual Network Consult AG (info@vnc.biz)
 *
 * This program is free software: you can redistribute it and/or modify
 * it under the terms of the GNU Affero General Public License as published by
 * the Free Software Foundation, version 3 of the License.
 *
 * This program is distributed in the hope that it will be useful,
 * but WITHOUT ANY WARRANTY; without even the implied warranty of
 * MERCHANTABILITY or FITNESS FOR A PARTICULAR PURPOSE. See the
 * GNU Affero General Public License for more details.
 *
 * You should have received a copy of the GNU Affero General Public License
 * along with this program. Look for COPYING file in the top folder.
 * If not, see http://www.gnu.org/licenses/.
 */


import { Injectable } from "@angular/core";
import { Subject } from "rxjs";

@Injectable({
  providedIn: "root"
})
export class ElectronService {
  app: any;
  remote: any;
  shell: any;
  win: any;
  ipcRenderer: any;
  desktopCapturer: any;
  store: any;
  onFocused$ = new Subject<any>();
  onBlurred$ = new Subject<any>();
  static downloadFile(url: string, fileName?: string) {
      const element = document.createElement("a");
      element.setAttribute("href", url);
      element.setAttribute("download", fileName);
      element.style.display = "none";
      document.body.appendChild(element);
      element.click();
      document.body.removeChild(element);
  }

  constructor() {
    if (this.isElectron) {
      this.remote = window.electron.remote;
      this.ipcRenderer = window.electron.ipcRenderer;
      this.desktopCapturer = {
        getSources: (opts) => this.ipcRenderer.invoke("DESKTOP_CAPTURER_GET_SOURCES", opts)
      };
      this.shell = window.electron.shell;
      this.app = this.remote.app;
      this.win = this.remote.getCurrentWindow();
      this.md5 = window.electron.blueimpMD5;
      const Store = window.electron.electronStore;
      this.store = new Store();

      // https://electronjs.org/docs/api/web-contents#contentssendchannel-arg1-arg2-
      this.ipcRenderer.on("app_cycle", (event, message) => {
        // console.log("[ElectronService][ipcRenderer on]", event, message);
        if (message === "focus") {
          // console.log("[ElectronService] app is focused");
          this.onFocused$.next(new Date().getSeconds());
        } else if (message === "blur") {
          // console.log("[ElectronService] app is blurred");
          this.onBlurred$.next(new Date().getSeconds());
        }
      });
    }
  }

  get isElectron(): boolean {
    return window && window.process && window.process.type;
  }

  get isWindows(): boolean {
    return window && window.process && window.process.platform === "win32";
  }

  get isMacOS(): boolean {
    return window && window.process && window.process.platform === "darwin";
  }

  get appVersion(): string {
    return this.app.getVersion();
  }

  setBadge(count: number): void {
    console.log("[setBadge]", count);
    if (this.isWindows) {
      this.ipcRenderer.sendSync("update-badge", count ? count : null);
    } else {
      this.app.setBadgeCount(count);

      if (this.isMacOS && count) {
        this.app.dock.bounce();
      }
    }
  }

  // Schemes this app legitimately hands to the OS:
  //   http/https/ftp - linkify() tags these with .open-new-window, which the click handler forwards here
  //   mailto         - the support link in task-help-faq-dialog.html carries the same class
  //   vnc*           - federated deeplinks; app-switcher derives the scheme from the app name,
  //                    so the set is open-ended rather than a fixed list
  // Everything else - file:, javascript:, data:, smb:, ms-msdt: and friends - is refused.
  private static readonly ALLOWED_URL_SCHEMES = /^(https?|ftp|mailto|vnc[a-z0-9]*):$/i;

  static isAllowedExternalUrl(url: string): boolean {
    if (typeof url !== "string" || url.trim() === "") {
      return false;
    }
    try {
      return ElectronService.ALLOWED_URL_SCHEMES.test(new URL(url).protocol);
    } catch {
      return false;
    }
  }

  openExternalUrl(url: string): void {
    if (!ElectronService.isAllowedExternalUrl(url)) {
      console.warn("[ElectronService][openExternalUrl] refused disallowed url:", url);
      return;
    }
    this.shell.openExternal(url);
  }

  showApp(): void {
    this.win.show();
  }

  md5(str: string): string {
    return this.md5(str);
  }

  setToStorage(key: string, value: any): void {
    this.store.set(key, value);
  }

  getFromStorage(key: string): any {
    return this.store.get(key);
  }

  deleteFromStorage(key: string): void {
    this.store.delete(key);
  }

  clearStorage(): void {
    this.store.clear();
  }
}
